import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { listSpaces, createSpace, createPage, updateSpace, SpaceRow, api } from "../lib/api";
import { SPACE_TEMPLATES } from "../lib/templates";
import GlobalHeader from "../components/GlobalHeader";

export default function DashboardPage() {
  const navigate = useNavigate();

  const [spaces, setSpaces] = useState<SpaceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "alpha">("newest");

  const [modalOpen, setModalOpen] = useState(false);
  const [formTemplate, setFormTemplate] = useState("blank");
  const [formSlug, setFormSlug] = useState("");
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [creatingMsg, setCreatingMsg] = useState("Create");
  const [createError, setCreateError] = useState<string | null>(null);

  const [tutorialSpace, setTutorialSpace] = useState<SpaceRow | null>(null);

  async function fetchSpaces() {
    setLoading(true);
    setError(null);
    try {
      const data = await listSpaces();
      setSpaces(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load spaces");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api.get<{ username_set: number }>("/api/me").then((me) => {
      if (me.username_set === 0) navigate("/setup", { replace: true });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchSpaces();
  }, []);

  function openModal() {
    setFormTemplate("blank");
    setFormSlug("");
    setFormName("");
    setFormDesc("");
    setCreateError(null);
    setModalOpen(true);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreatingMsg("Creating space...");
    setCreateError(null);
    try {
      const space = await createSpace({
        slug: formSlug,
        name: formName,
        description: formDesc || undefined,
      });

      const template = SPACE_TEMPLATES.find((t) => t.id === formTemplate);
      if (template && template.pages.length > 0) {
        if (template.agentInstructions) {
          await updateSpace(space.username, space.slug, { agent_instructions: template.agentInstructions });
        }
        setCreatingMsg(`Creating pages (0/${template.pages.length})...`);
        for (let i = 0; i < template.pages.length; i++) {
          const p = template.pages[i];
          setCreatingMsg(`Creating pages (${i + 1}/${template.pages.length})...`);
          await createPage(space.username, space.slug, { ...p, publish: true });
        }
      }

      setModalOpen(false);
      setTutorialSpace(space);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create space");
    } finally {
      setCreating(false);
      setCreatingMsg("Create");
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <GlobalHeader activeTab="spaces" />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My Spaces</h1>
          <button
            onClick={openModal}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            New space
          </button>
        </div>

        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <SpaceCardSkeleton key={i} />)}
          </div>
        )}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}
        {!loading && !error && spaces.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No spaces yet. Create your first space!</p>
        )}
        {!loading && !error && spaces.length > 0 && (() => {
          const filteredSpaces = spaces
            .filter((s) => {
              if (!search) return true;
              const q = search.toLowerCase();
              return s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q) || (s.description ?? "").toLowerCase().includes(q);
            })
            .sort((a, b) => {
              if (sortBy === "alpha") return a.name.localeCompare(b.name);
              if (sortBy === "oldest") return a.created_at.localeCompare(b.created_at);
              return b.created_at.localeCompare(a.created_at); // newest
            });
          return (
            <>
              <div className="flex items-center gap-3 mb-6">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search spaces..."
                  className="flex-1 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="alpha">A–Z</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSpaces.map((space) => (
                  <Link
                    key={space.id}
                    to={`/${space.username}/${space.slug}`}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 hover:border-blue-400 dark:hover:border-blue-600 transition-colors block"
                  >
                    <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 truncate">{space.name}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">@{space.username}/{space.slug}</p>
                    {space.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-3">{space.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {space.page_count ?? 0} {space.page_count === 1 ? "page" : "pages"}
                      </span>
                      {(space.pending_draft_count ?? 0) > 0 && (
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 inline-block" />
                          {space.pending_draft_count} pending {space.pending_draft_count === 1 ? "draft" : "drafts"}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          );
        })()}
      </main>

      {/* New Space Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-5">New Space</h2>
            <form onSubmit={handleCreate} className="space-y-5">

              {/* Template selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {SPACE_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setFormTemplate(t.id)}
                      className={`text-left p-3 rounded-lg border-2 transition-colors ${
                        formTemplate === t.id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                    >
                      <div className="text-xl mb-1">{t.icon}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value)}
                  required
                  placeholder="my-space"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  placeholder="My Space"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  rows={2}
                  placeholder="Optional description"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {createError && <p className="text-red-600 dark:text-red-400 text-sm">{createError}</p>}

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {creating ? creatingMsg : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tutorial Modal */}
      {tutorialSpace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🎉</span>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Space "{tutorialSpace.name}" created!
              </h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              Next, connect an AI agent so it can read and write to this space.
            </p>
            <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300 mb-6">
              <li className="flex gap-3">
                <span className="shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center">1</span>
                <span>Go to <Link to="/agents" className="text-blue-600 dark:text-blue-400 hover:underline">Agents</Link> and create an agent with an API key.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center">2</span>
                <span>In <Link to={`/${tutorialSpace.username}/${tutorialSpace.slug}`} className="text-blue-600 dark:text-blue-400 hover:underline">Space Settings → Agent Access</Link>, click <strong>Invite Agent</strong> and grant <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs">write</code> permission.</span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center">3</span>
                <span>Add this skill to your agent's <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs">CLAUDE.md</code>:</span>
              </li>
            </ol>
            <div className="bg-gray-950 rounded-lg p-3 text-xs font-mono text-gray-300 mb-6">
              Skills:<br />
              {"  "}- https://wiki.0xkaz.com/skill.md
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setTutorialSpace(null)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 transition-colors"
              >
                Do this later
              </button>
              <Link
                to={`/${tutorialSpace.username}/${tutorialSpace.slug}`}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Go to Space Settings →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpaceCardSkeleton() {
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 animate-pulse">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
      <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-full mb-2" />
      <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-4/5" />
    </div>
  );
}
